/**
 * What a community's pull and push print (gh #5003): server text sanitized, a text's changed lines,
 * and the sections one direction lists — shared by the settings, rooms and courses halves.
 */

import { sanitizeTerminalText } from '../lib/terminal-output.js';

/** Server text (and file text) as printed for a person. */
export const show = (text: string) => JSON.stringify(sanitizeTerminalText(text));

/** The lines that change between two texts, `-` then `+`; a whole replacement past a size bound. */
export function lineDiff(from: string | null, to: string | null): string[] {
  return lineDiffOf(
    from ? sanitizeTerminalText(from).split('\n') : [],
    to ? sanitizeTerminalText(to).split('\n') : [],
  );
}

/** `lineDiff` over lines already split and sanitized, where an empty line is a line. */
export function lineDiffOf(a: string[], b: string[]): string[] {
  if (a.length * b.length > 250_000) return [...a.map((l) => `- ${l}`), ...b.map((l) => `+ ${l}`)];
  const lcs = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: string[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      i++;
      j++;
    } else if (j >= b.length || (i < a.length && lcs[i + 1][j] >= lcs[i][j + 1])) {
      out.push(`- ${a[i++]}`);
    } else {
      out.push(`+ ${b[j++]}`);
    }
  }
  return out;
}

/** What one direction would change, as printed lines. Shared by push (server → files) and pull (files → server). */
export interface Listing {
  changes: string[];
  serverChanged: string[];
  conflicts: string[];
  leftAsIs: string[];
  kept: string[];
}

export const emptyListing = (): Listing => ({ changes: [], serverChanged: [], conflicts: [], leftAsIs: [], kept: [] });

/** A titled block of indented lines; nothing at all when there are no lines. */
export function printSection(title: string, lines: string[]): void {
  if (lines.length === 0) return;
  console.log(`\n${title}`);
  for (const line of lines) console.log(`  ${line}`);
}

export function printListing(listing: Listing): void {
  printSection('Changes:', listing.changes);
  printSection('Changed on the server since your last pull — left as is; pull to take it:', listing.serverChanged);
  printSection('Kept — edited in your files, not pushed yet:', listing.kept);
  printSection('Not in your files — left as is:', listing.leftAsIs);
  printSection('Conflicts — changed in your files and on the server since your last pull:', listing.conflicts);
}
